import React, { useEffect, useRef, useState } from "react";
import useNumericInput from "../../../hooks/useNumericInput";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { useLoader } from "../../../utils/LoaderUtils";
import AsyncSelect from "react-select/async";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";
import dealAPI from "../../../api/promo/dealApi";
import SubmitButton from "../../../components/partials/global/SubmitButton";
import ProductAPI from "../../../api/inventory/ProductApi";

const EMPTY_ROW = {
  productId: "",
  productLabel: "",
  variantId: "",
  variants: [],
  hasVariants: false,
  originalPrice: "",
  dealPrice: "",
  discountType: "PERCENTAGE",
  discountValue: "",
  totalQuantity: "",
  dealDate: "",
};

function AddNew() {
  useNumericInput();

  const today = new Date().toISOString().split("T")[0];
  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);

  // Product loader
  const loadProducts = async (inputValue) => {
    try {
      const res = await ProductAPI.listProductsforDeal();

      return res.data.products.map((item) => ({
        label: item.title,
        value: item.id,
        basePrice: item.basePrice,
        hasVariants: item.hasVariants,
        variants: item.variants || [],
      }));
    } catch (err) {
      return [];
    }
  };

  // ─── Row handlers ────────────────────────────────────────
  const addRow = () => setRows([...rows, { ...EMPTY_ROW }]);
  const removeRow = (index) => setRows(rows.filter((_, i) => i !== index));

  const updateRow = (index, updates) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...updates } : row)),
    );
  };

  // ─── Auto-calculate dealPrice ────────────────────────────
  const calculateDealPrice = (originalPrice, discountType, discountValue) => {
    const op = Number(originalPrice);
    const dv = Number(discountValue);
    if (!op || !dv) return "";

    if (discountType === "PERCENTAGE") {
      if (dv >= 1 && dv <= 99) return Math.round(op - (op * dv) / 100);
    } else {
      if (dv > 0 && dv < op) return op - dv;
    }
    return "";
  };

  // Auto-recalc whenever discount changes
  const handleDiscountChange = (index, field, value) => {
    const row = rows[index];
    const updated = { ...row, [field]: value };

    // Empty allow karo, warna number bana ke check karo
    if (field === "discountValue" && value !== "") {
      const num = Number(value);
      if (row.discountType === "PERCENTAGE") {
        if (num < 1 || num > 99) return; // out of range
      } else {
        if (num < 1) return;
      }
    }

    updated.dealPrice = calculateDealPrice(
      updated.originalPrice,
      updated.discountType,
      updated.discountValue,
    );

    updateRow(index, updated);
  };

  // Naya handler — originalPrice change pe dealPrice recalc
  const handleOriginalPriceChange = (index, value) => {
    const row = rows[index];
    const updated = { ...row, originalPrice: value };

    // Agar discountValue pehle se hai toh dealPrice recalc karo
    updated.dealPrice = calculateDealPrice(
      value,
      row.discountType,
      row.discountValue,
    );

    updateRow(index, updated);
  };

  // ─── Product select ──────────────────────────────────────
  const handleProductSelect = (index, selected) => {
    if (!selected) {
      updateRow(index, EMPTY_ROW);
      return;
    }
    updateRow(index, {
      productId: selected.value,
      productLabel: selected.label,
      hasVariants: selected.hasVariants,
      variants: selected.variants,
      variantId: "",
      originalPrice: selected.hasVariants ? "" : selected.basePrice || "",
      dealPrice: "",
      discountValue: "",
    });
  };

  // ─── Variant select ──────────────────────────────────────
  const handleVariantSelect = (index, variantId) => {
    const row = rows[index];
    const variant = row.variants.find((v) => v.id === variantId);
    if (!variant) return;
    updateRow(index, {
      variantId,
      originalPrice: variant.price, // default fill, but editable
      dealPrice: "",
      discountValue: "",
    });
  };

  // ─── Validate ────────────────────────────────────────────
  const validate = () => {
    const newErrors = {};
    rows.forEach((row, i) => {
      if (!row.productId) newErrors[`${i}.productId`] = "Product required";
      if (row.hasVariants && !row.variantId)
        newErrors[`${i}.variantId`] = "Variant required";
      if (!row.originalPrice) newErrors[`${i}.originalPrice`] = "Required";
      if (!row.dealPrice) newErrors[`${i}.dealPrice`] = "Required";
      if (!row.discountValue) newErrors[`${i}.discountValue`] = "Required";
      if (!row.totalQuantity) newErrors[`${i}.totalQuantity`] = "Required";
      if (!row.dealDate) newErrors[`${i}.dealDate`] = "Required";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ──────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      deals: rows.map((r) => ({
        productId: r.productId,
        variantId: r.variantId || null,
        originalPrice: Number(r.originalPrice),
        dealPrice: Number(r.dealPrice),
        discountType: r.discountType,
        discountValue: Number(r.discountValue),
        totalQuantity: Number(r.totalQuantity),
        dealDate: r.dealDate,
      })),
    };

    setIsLoading(true);
    showLoader();

    try {
      const res = await dealAPI.addDetails(payload);
      if (res.code === 200) {
        showSuccess(res.message);
        setRows([{ ...EMPTY_ROW }]);
        // Failed rows handle karo agar aaye
        if (res.data.failed?.length) {
          console.warn("Some deals failed:", res.data.failed);
        }
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Create Deal</h4>
              <h6>Create and manage deal of the day offers.</h6>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Deal Rows ({rows.length})</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              {rows.map((row, index) => (
                <div key={index} className="border rounded p-3 mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <strong>Deal #{index + 1}</strong>
                    {rows.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeRow(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-lg-4 mb-2">
                      <label className="form-label">
                        Product <span className="text-error">*</span>
                      </label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={loadProducts}
                        placeholder="Search Product"
                        value={
                          row.productId
                            ? { value: row.productId, label: row.productLabel }
                            : null
                        }
                        onChange={(s) => handleProductSelect(index, s)}
                      />
                      {errors[`${index}.productId`] && (
                        <span className="text-danger">
                          {errors[`${index}.productId`]}
                        </span>
                      )}
                    </div>

                    {/* Variant — sirf jab hasVariants true ho */}
                    {row.hasVariants && (
                      <div className="col-lg-4 mb-2">
                        <label className="form-label">
                          Variant <span className="text-error">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={row.variantId}
                          onChange={(e) =>
                            handleVariantSelect(index, e.target.value)
                          }
                        >
                          <option value="">Select Variant</option>
                          {row.variants.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.title} (₹{v.price})
                            </option>
                          ))}
                        </select>
                        {errors[`${index}.variantId`] && (
                          <span className="text-danger">
                            {errors[`${index}.variantId`]}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Deal Date */}
                    <div className="col-lg-4 mb-2">
                      <label className="form-label">
                        Deal Date <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        min={today}
                        className="form-control"
                        value={row.dealDate}
                        onChange={(e) =>
                          updateRow(index, { dealDate: e.target.value })
                        }
                      />
                      {errors[`${index}.dealDate`] && (
                        <span className="text-danger">
                          {errors[`${index}.dealDate`]}
                        </span>
                      )}
                    </div>

                    {/* Original Price (read-only — auto from product/variant) */}
                    <div className="col-lg-3 mb-2">
                      <label className="form-label">
                        Original Price <span className="text-error">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Price"
                        value={row.originalPrice}
                        onChange={(e) =>
                          handleOriginalPriceChange(index, e.target.value)
                        }
                      />
                      {errors[`${index}.originalPrice`] && (
                        <span className="text-danger">
                          {errors[`${index}.originalPrice`]}
                        </span>
                      )}
                    </div>

                    {/* Discount Type */}
                    <div className="col-lg-3 mb-2">
                      <label className="form-label">
                        Discount Type <span className="text-error">*</span>
                      </label>
                      <select
                        className="form-control"
                        value={row.discountType}
                        onChange={(e) =>
                          updateRow(index, {
                            discountType: e.target.value,
                            discountValue: "",
                            dealPrice: "",
                          })
                        }
                      >
                        <option value="PERCENTAGE">Percentage (%)</option>
                        <option value="FLAT">Flat Amount (₹)</option>
                      </select>
                    </div>

                    {/* Discount Value */}
                    <div className="col-lg-3 mb-2">
                      <label className="form-label">
                        {row.discountType === "PERCENTAGE"
                          ? "Percentage (%)"
                          : "Flat (₹)"}{" "}
                        *
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder={
                          row.discountType === "PERCENTAGE"
                            ? "e.g. 10"
                            : "e.g. 500"
                        }
                        value={row.discountValue}
                        onChange={(e) =>
                          handleDiscountChange(
                            index,
                            "discountValue",
                            e.target.value,
                          )
                        }
                      />
                      {errors[`${index}.discountValue`] && (
                        <span className="text-danger">
                          {errors[`${index}.discountValue`]}
                        </span>
                      )}
                    </div>

                    {/* Deal Price (auto-calculated, read-only) */}
                    <div className="col-lg-3 mb-2">
                      <label className="form-label">Deal Price (Auto)</label>
                      <input
                        type="text"
                        className="form-control bg-success-subtle"
                        value={row.dealPrice}
                        readOnly
                      />
                    </div>

                    {/* Total Quantity */}
                    <div className="col-lg-3 mb-2">
                      <label className="form-label">
                        Total Quantity <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control only-numeric"
                        placeholder="Enter Quantity"
                        value={row.totalQuantity}
                        onChange={(e) =>
                          updateRow(index, { totalQuantity: e.target.value })
                        }
                      />
                      {errors[`${index}.totalQuantity`] && (
                        <span className="text-danger">
                          {errors[`${index}.totalQuantity`]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addRow}
                >
                  + Add Another
                </button>
                <SubmitButton
                  isLoading={isLoading}
                  text={`Submit ${rows.length} Deal${rows.length > 1 ? "s" : ""}`}
                  loadingText="Submitting..."
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddNew;
